import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { KeyRound, X, Loader2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { PIN_BYPASS_ENABLED } from "../lib/pinConfig";

/**
 * Feb 2026 · PIN-Gate für Fremd-Concierge-Bearbeitung.
 *
 * Aktiviert sich, sobald der axios-Interceptor einen Write in einer
 * Fremd-Concierge-Ansicht blockiert (siehe `lib/api.js::pinPromptRequester`).
 *
 * Feb 2026 · 16 Sep 2026 · User-Directive „PIN-Logik für Admin korrigieren":
 *   „Der Admin bestätigt immer mit seinem eigenen PIN."
 * Der Admin gibt hier seine EIGENE persönliche 4-stellige PIN ein
 * (`db.users.team_pin_hash` — dieselbe, mit der er Buchungen speichert),
 * NICHT mehr die zentrale Admin-PIN (0611). Nach `POST /api/me/team-pin/verify`
 * legt der AuthContext das `otherConciergeUnlocked`-Flag um.
 *
 * Reload oder Wechsel des impersonierten Concierges resettet den Unlock
 * automatisch (siehe `auth.js::setImpersonatedConcierge`).
 */
export default function PinUnlockDialog() {
  const {
    pinPromptOpen, closePinPrompt,
    unlockOtherConcierge, impersonatedConcierge,
  } = useAuth();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (pinPromptOpen) {
      setPin("");
      // Feb 2026 · 10 Feb 2026 · TEMPORÄRER PIN-BYPASS (siehe lib/pinConfig.js).
      // Statt Modal zu öffnen und Backend-Verify aufzurufen, direkt
      // freischalten. Nach Deployment: PIN_BYPASS_ENABLED = false.
      if (PIN_BYPASS_ENABLED) {
        unlockOtherConcierge();
        closePinPrompt();
        return;
      }
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [pinPromptOpen, unlockOtherConcierge, closePinPrompt]);

  useEffect(() => {
    if (!pinPromptOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closePinPrompt();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pinPromptOpen, closePinPrompt]);

  if (!pinPromptOpen) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{4}$/.test(pin)) {
      toast.error("Bitte 4 Ziffern eingeben.");
      return;
    }
    setBusy(true);
    try {
      // Feb 2026 · 16 Sep 2026 · Directive „PIN-Logik für Admin korrigieren":
      // Der Admin nutzt HIER seine eigene persönliche Team-PIN (nicht mehr
      // die zentrale Admin-PIN 0611). Endpoint prüft gegen
      // `db.users.team_pin_hash` des angemeldeten Admins und loggt
      // best-effort dieselbe `unlock`-Audit-Zeile wie der Alt-Endpoint.
      await api.post("/me/team-pin/verify", {
        pin,
        impersonated_concierge_id: impersonatedConcierge?.id || null,
        impersonated_concierge_name: impersonatedConcierge?.name || null,
        purpose: impersonatedConcierge?.id ? "other-concierge-edit" : "sensitive-edit",
      });
      unlockOtherConcierge();
      toast.success("Bearbeitung freigeschaltet. Bitte erneut speichern.");
      closePinPrompt();
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("PIN ist falsch.");
      } else if (err.response?.status === 428
        || err.response?.data?.detail?.code === "team_pin_not_set") {
        // Admin hat noch keine persönliche PIN gesetzt.
        toast.error(
          'Bitte richten Sie zuerst Ihre persönliche PIN unter „Team-Profil / Persönliche PIN" ein.',
        );
        closePinPrompt();
      } else {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
      }
      setPin("");
      setTimeout(() => inputRef.current?.focus(), 20);
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: withAlpha(BRAND_COLORS.navy, 0.55) }}
      onClick={closePinPrompt}
      data-testid="pin-unlock-dialog"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pin-unlock-title"
        className="bg-white w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={closePinPrompt}
          className="absolute top-3 right-3 text-navy-dark/50 hover:text-navy"
          aria-label="Schließen"
          data-testid="pin-unlock-close"
        >
          <X className="w-4 h-4" strokeWidth={1.6} />
        </button>
        <form onSubmit={submit} className="p-6 md:p-8">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center mb-4"
            style={{ background: withAlpha(BRAND_COLORS.gold, 0.15), color: BRAND_COLORS.goldDark }}
          >
            <KeyRound className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <h2
            id="pin-unlock-title"
            className="font-display text-2xl text-navy leading-tight mb-1"
            data-testid="pin-unlock-title"
          >
            Ihre eigene PIN eingeben
          </h2>
          <p className="text-[13px] text-navy-dark/70 leading-relaxed mb-5">
            Sie sehen die Ansicht{impersonatedConcierge?.name ? (
              <> von <b>{impersonatedConcierge.name}</b></>
            ) : " eines anderen Concierges"}. Bitte bestätigen Sie mit{" "}
            <b>Ihrer eigenen persönlichen PIN</b> — nicht mit der PIN des
            Concierges. Die Freischaltung gilt bis zum nächsten Concierge-Wechsel.
          </p>
          <label
            htmlFor="pin-unlock-input"
            className="label-caps block mb-2"
            style={{ color: BRAND_COLORS.gold }}
          >
            Ihre persönliche PIN
          </label>
          <input
            id="pin-unlock-input"
            ref={inputRef}
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            data-testid="pin-unlock-input"
            className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-[18px] tracking-[0.5em] text-center text-navy tabular-nums"
            placeholder="••••"
            disabled={busy}
          />
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={closePinPrompt}
              disabled={busy}
              data-testid="pin-unlock-cancel"
              className="text-[11px] tracking-[0.15em] uppercase text-navy-dark/60 hover:text-navy px-3 py-2"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={busy || pin.length !== 4}
              data-testid="pin-unlock-submit"
              className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
              style={{ background: BRAND_COLORS.navy }}
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
              Bestätigen
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
